import { ProgressOverlayStatus } from '@fs/zion-ui'
import { css } from '@emotion/core'
import { i18n } from '@fs/zion-locale'
import zionDebug from '@fs/zion-debug'

import { isEmbeddedMobile } from '../isMobile'
import { BASE_KEEP_FONTS, KEEP_PROCESS_TYPE } from './constants'
import { convertImagesToDataUrls, dataURLtoFile, getXPathForElement } from './utils'
import htmlToOutput from './htmlToOutput'
import convertFontsToBase64 from './convertFontsToBase64'

const { PRINT } = KEEP_PROCESS_TYPE
const { FAILED, IN_PROGRESS, SUCCESSFUL } = ProgressOverlayStatus
const debugError = zionDebug('zh:keepOutput:error')
const debugInfo = zionDebug('zh:keepOutput:info')

const keepIframeCss = css`
  inset-block-start: 0px;
  inset-inline-start: -100vw;
  position: absolute;
  visibility: hidden;
`

const IFRAME_ID = `keep-output-${window?.crypto?.randomUUID?.() || Date.now()}`

// https://stackoverflow.com/a/70585394/2344083
const isWebkit = 'GestureEvent' in window
// https://stackoverflow.com/a/52855084/2344083
const isTouchDevice = window.matchMedia('(pointer: coarse)').matches
// https://stackoverflow.com/a/35813965/2344083
export const isSafari = !/(CriOS|FxiOS|OPiOS|mercury|chrome)/i.test(window.navigator.userAgent)

// Download
async function save({ filename, fileType, iosSafariDownload, onProgress, output, worker }) {
  const showSuccessMessage = () => {
    const message =
      fileType.current === 'pdf'
        ? i18n.t(
            'keepOutput.onProgress.downloadDonePdf',
            'Download complete. Now you can use it for printing and sharing.'
          )
        : i18n.t(
            'keepOutput.onProgress.downloadDoneImage',
            'Download complete. Now you can share it with your friends and family.'
          )
    onProgress({ message, status: SUCCESSFUL })
  }

  // The iOS Family Tree and Together apps don’t handle blob downloading in the embedded webview, but the native share works well
  if (isEmbeddedMobile() && isWebkit) {
    try {
      const fileExtension = fileType.current === 'pdf' ? 'pdf' : 'jpg'
      const dataUrl = output?.output?.('dataurlstring') || output
      const sharedFile = await dataURLtoFile(dataUrl, `${filename.current}.${fileExtension}`)
      navigator.share({ files: [sharedFile], title: filename.current })
      showSuccessMessage()
    } catch (error) {
      debugInfo('navigator.share failed or was canceled', error)
    }
    return
  }
  // This is the reason that iOS Safari opens the blob in a url instead of downloading.
  // https://github.com/eligrey/FileSaver.js#ios
  // Due to restrictions in iOS Safari saveAs opens in a new window instead of downloading
  // Since we need to open the file in a new window we need a url.
  // Since the file isn’t saved anywhere we use server side session storage.
  // This storage is only valid for one load of the page.  If you refresh the file is gone.
  // Most apps know how to handle blob urls;  Line App does not do a very good job at this.
  // We cannot save the file anywhere because of privacy concerns and approval.
  // This works around the issue by showing the file using the session if it exists or
  // the normal design page.
  if (iosSafariDownload?.isOn && isWebkit && isTouchDevice && isSafari) {
    debugInfo('Will save to session storage - client')

    const fileExtension = fileType.current === 'pdf' ? 'pdf' : 'jpg'
    // Save the file to session storage on the server
    const dataUrl = output?.output?.('dataurlstring') || output
    let dataUrlWithFilename = dataUrl.replace(/filename=.*?;/, `filename=${filename.current}.${fileExtension};`)
    if (!/filename=.*?;/.test(dataUrlWithFilename)) {
      dataUrlWithFilename = dataUrlWithFilename.replace(
        /(data:.*?;)/,
        `$1filename=${filename.current}.${fileExtension};`
      )
    }

    await fetch(`${window.SERVER_DATA.appPath}/download`, {
      method: 'POST',
      body: dataUrlWithFilename,
      headers: {
        // Get around Frontier/snowApp bodyParser limit
        // by using text instead of JSON which Frontier has
        // limited to 1mb
        'Content-Type': 'text/plain',
      },
    })

    showSuccessMessage()

    debugInfo('Saved to session storage - client')

    // Use window.location = window.location.href instead of
    // window.location.reload() to ensure that the browser
    // back button works as expected
    // Reloading will show the file if it is in session storage
    window.location = window.location.href

    return
  }

  // Open in new tab (and download for JPEG only)
  if (fileType.current === 'pdf') {
    // For PDF: only open in new tab with security settings (user can download from browser if desired)
    const pdfBlobUrl = output.output('bloburl')
    window.open(pdfBlobUrl, '_blank', 'noopener,noreferrer')
  } else {
    // For JPEG: use worker.save() which opens in new tab AND triggers download
    await worker.save()
  }
  showSuccessMessage()
}

async function iframeOnLoad({ filename, fileType, iframe, iosSafariDownload, isCanceled, onProgress, output, worker }) {
  // Properly wait for the iframe to load
  await new Promise((resolveWaitForLoad) => {
    const loadedInterval = setInterval(async () => {
      try {
        if (iframe.contentWindow?.document) {
          clearInterval(loadedInterval)
          resolveWaitForLoad()
        }
      } catch (error) {
        // If we don't have access to the iframe
        // Cross-origin or localhost
        clearInterval(loadedInterval)
        await new Promise(() => {
          setTimeout(resolveWaitForLoad, 1000)
        })
      }
    }, 100)
  })

  let fallbackRan = false
  let onafterprintRan = false
  let windowDidBlur = false
  window.addEventListener(
    'blur',
    () => {
      windowDidBlur = true
      // Other methods of detecting when the dialog is rendered or
      // print/cancel is clicked appear to be not reliable cross-browser
      // or simply will not work.
      // `window.onafterprint/onbeforeprint` doesn't work consistently
      // Detection using `@media print` media query doesn't work because it isn't set
      // when using the html2pdf library.
      if (!fallbackRan && !onafterprintRan) {
        onProgress({
          message: i18n.t('keepOutput.onProgress.printDialogOpened', 'Print Dialog Opened'),
          status: SUCCESSFUL,
        })
      }
    },
    { once: true }
  )

  if (isCanceled.current) {
    onProgress({ message: i18n.t('keepOutput.onProgress.canceled', 'Canceled'), status: SUCCESSFUL })
    return
  }

  try {
    if (iframe.contentWindow?.onafterprint) {
      iframe.contentWindow.onafterprint = function onafterprint() {
        if (!windowDidBlur && !fallbackRan) {
          onafterprintRan = true
          onProgress({
            message: i18n.t('keepOutput.onProgress.printDialogOpened', 'Print Dialog Opened'),
            status: SUCCESSFUL,
          })
        }
      }
    }
  } catch (error) {
    debugError(error)
    // Do Nothing
  }

  // If available use this instead of `output.autoPrint()` to ensure we have more control
  // over when we are actually ready to print.
  try {
    iframe.contentWindow?.print?.()
  } catch (error) {
    debugError(error)
    // https://stackoverflow.com/a/22695248/2344083
    // Mobile "Chrome blocks the .print() method if the iframe contents are not from the same origin.";
    // blob appears to not be on the same origin; save instead.
    save({ filename, fileType, iosSafariDownload, onProgress, output, worker })
  }

  // Set to success as a fallback if 'blur' doesn't work for some reason.
  setTimeout(() => {
    if (!onafterprintRan && !windowDidBlur) {
      fallbackRan = true
      onProgress({
        message: i18n.t('keepOutput.onProgress.printDialogOpened', 'Print Dialog Opened'),
        status: SUCCESSFUL,
      })
    }
  }, 5000)
}

export default async function keepOutput({
  cancelOverlay,
  dpi,
  elementRef,
  fileType,
  filename,
  fontElements = [],
  fonts = [],
  imageQuality,
  imageScale,
  imageSize,
  iosSafariDownload,
  isCanceled,
  keepStyles,
  onProgress = () => {},
  maxCanvasSize,
  pageOrientation,
  pageSelector,
  pageSize,
  process,
  shouldCenter,
  totalPages,
  fromMemoryBooks,
}) {
  const xpath = getXPathForElement(elementRef.current, document)
  if (!xpath || !elementRef?.current) {
    isCanceled.current = true
    cancelOverlay.close()
    onProgress({
      message: i18n.t('keepOutput.onProgress.failed', 'Failed'),
      status: FAILED,
    })
    return null
  }

  // `convertImagesToDataUrls` rewrites the live <img> src attributes to data URLs so html2canvas
  // can read them cross-origin. Snapshot the originals so we can restore them after rendering:
  // otherwise the images stay as data URLs, the next generation skips them (they already start
  // with `data:`), and html2canvas's foreignObject render comes back blank ("Unable to generate
  // image") — which is why a JPEG only worked once until the page was refreshed.
  const imagesForRestore = Array.from(elementRef.current.querySelectorAll('img')).map((image) => [
    image,
    image.getAttribute('src'),
  ])
  const restoreImageSrcs = () => {
    imagesForRestore.forEach(([image, src]) => {
      if (src != null && image.getAttribute('src') !== src) {
        image.setAttribute('src', src)
      }
    })
  }

  try {
    !fromMemoryBooks &&
      onProgress({
        message: i18n.t('keepOutput.onProgress.imagesProcessing', 'Processing Images'),
        status: IN_PROGRESS,
      })
    await convertImagesToDataUrls(elementRef)
  } catch (error) {
    restoreImageSrcs()
    onProgress({ message: i18n.t('keepOutput.onProgress.imagesFailed', 'Unable to Process Images'), status: FAILED })
    isCanceled.current = true
    cancelOverlay.close()
    return null
  }

  !fromMemoryBooks &&
    onProgress({
      message: i18n.t('keepOutput.onProgress.retrieving', 'Retrieving Output'),
      status: IN_PROGRESS,
    })

  let output
  let worker
  try {
    document.querySelectorAll('[data-id="keep-font-face"]').forEach((style) => {
      style.remove()
    })
    if (fonts.current?.length) {
      let perPageFontElements = fontElements.current
      if (totalPages.current > 1) {
        perPageFontElements = fontElements.current
          .map((fontElement) => {
            return Array.from(fontElement.querySelectorAll(pageSelector?.current))
          })
          .flat()
      }
      const elements = [...(perPageFontElements || []), ...(document.querySelectorAll('svg:not([role="img"]') || [])]
      const base64KeepFontsCss = await convertFontsToBase64({ fonts: fonts.current.map((font) => font.toLowerCase()) })
      elements.forEach((element) => {
        const style = document.createElement('style')
        style.setAttribute('data-id', 'keep-font-face')
        style.innerHTML = base64KeepFontsCss
        element.prepend(style)
      })
    }

    const retrievedOutput = await htmlToOutput({
      dpi: dpi?.current,
      element: elementRef.current,
      elementXpath: xpath,
      fileType: fileType?.current,
      filename: filename?.current,
      imageQuality: imageQuality?.current,
      imageScale: imageScale?.current,
      imageSize: imageSize?.current,
      keepStyles: keepStyles?.current,
      maxCanvasSize,
      onPageProcessing: (page) => {
        onProgress({
          message: `${i18n.t('keepOutput.onProgress.retrieving', 'Retrieving Output')} - ${page}/${
            totalPages?.current
          }`,
          status: IN_PROGRESS,
        })
      },
      pageSelector: pageSelector?.current,
      pageOrientation: pageOrientation?.current,
      pageSize: pageSize?.current,
      process,
      processFonts: fonts.current?.length,
      // Only designs with their own typography (fonts beyond the base set) need html2canvas's
      // manual DOM renderer. Base-only designs — e.g. the SVG-based pedigree trees — must stay on
      // foreignObject rendering, which the manual renderer would break (dropped connector lines,
      // missing box shadows, mis-spaced text).
      useManualFontRendering: fonts.current?.some((font) => !BASE_KEEP_FONTS.includes(font.toLowerCase())),
      shouldCenter: shouldCenter?.current,
      totalPages: totalPages?.current,
    })
    output = retrievedOutput.pdf || retrievedOutput.image

    if (fromMemoryBooks) {
      return await dataURLtoFile(output, `${filename?.current}.${fileType?.current}`)
    }

    worker = retrievedOutput.worker

    if (!output || !worker) {
      throw new Error('Unable to retrieve output')
    }
  } catch (error) {
    debugError('Could not retrieve output.', error)
    isCanceled.current = true
    cancelOverlay.close()
    onProgress({ message: i18n.t('keepOutput.onProgress.failed', 'Failed'), status: FAILED })
    return null
  } finally {
    // Rendering is done by this point (the blob/pdf is already produced), so restore the live
    // images to their original srcs for the next generation.
    restoreImageSrcs()
  }

  if (isCanceled.current) {
    onProgress({ message: i18n.t('keepOutput.onProgress.canceled', 'Canceled'), status: SUCCESSFUL })
    return null
  }

  // It appears as though Firefox on Android does not support the `print` method
  // Also, used for downloading
  if (!window?.print || process !== PRINT) {
    await save({ filename, fileType, iosSafariDownload, onProgress, output, worker })
    return null
  }

  onProgress({
    message: i18n.t('keepOutput.onProgress.generating', 'Opening Print Dialog'),
    status: IN_PROGRESS,
  })

  // In order to print while remaining on the same page, we need to create an iframe.
  // Only remove an old iframe if we are trying to create another one,
  // this allows the print dialog to remain open; unless we are using webkit.
  document.querySelector(`#${IFRAME_ID}`)?.remove()
  const iframe = document.createElement('iframe')
  iframe.setAttribute('id', IFRAME_ID)
  iframe.style.cssText = keepIframeCss.styles
  iframe.onload = () => iframeOnLoad({ fileType, iframe, iosSafariDownload, isCanceled, onProgress, output, worker })
  iframe.src = output.output('bloburl')
  document.body.appendChild(iframe)
  return null
}
